import { Fragment, useEffect, useMemo } from "react";

import { WatingApproval, BrowserRegistrationModal } from "components";
import { useInstance, useTranslation } from "hooks";
import { Modal } from "@storybook/new-modal";
import {
  SIGNIN_SCREENS,
  SigninForm,
  SigninMethods,
  SignInSkelton,
  useSignin,
} from "./";

import "./signin.scss";
import { useSignUp } from "views/signup";
import { Loader } from "@storybook/loader";

interface ISignIn {
  loading?: boolean; // Optional prop to indicate if loading is in progress
}

/**
 * SignIn Component
 * @param {Object} props - Component props
 * @param {boolean} [props.loading] - Optional loading state to control the display of the loading skeleton
 */
let isHandleUserVerfiedCalled = false;

export const SignIn = ({ loading }: ISignIn) => {
  // Hook to get translation functions
  const { t } = useTranslation();
  const { instanceDetails } = useInstance();

  const { manageDefaultUser } = useSignUp();

  const {
    signInCurrentScreen,
    setSignInInCurrentScreen,
    currentAuthMethod,
    form,
    handleAuthMethodOptions,
    handleVerifiedUser,
    verified_user,
    handleLoginViaLink,
    showBrowserRegistrationModal,
    setShowBrowserRegistrationModal,
    showBrowserRegistrationWaitingModal,
    setShowBrowserRegistrationWaitingModal,
    handleBrowserRegistrationResend,
    registerWebAuthnWithAuthSource,
    passkeyRegistrationApprovalStatus,
    handleApprovalBackToMethod,
    handleApprovalBackToSigninPage,
    setLoadingMethodSelection,
  } = useSignin(manageDefaultUser);

  const { SIGNIN, LOADING, METHODS, APPROVAL } = SIGNIN_SCREENS;

  // Create status JSX based on passkey registration approval status
  const getStatusIcon = useMemo(() => {
    const statusStyles = {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
    };

    switch (passkeyRegistrationApprovalStatus) {
      case "APPROVED":
        return (
          <div
            style={{
              ...statusStyles,
              backgroundColor: "#10B981", // Green background
              color: "#ffffff",
            }}
            className="zoom-animation"
          >
            <i key="approved" className="ri-check-line " />
          </div>
        );
      case "REJECTED":
        return (
          <div
            style={{
              ...statusStyles,
              backgroundColor: "#EF4444", // Red background
              color: "#ffffff",
            }}
            className="zoom-animation"
          >
            <i key="rejected" className="ri-close-line" />
          </div>
        );
      case "pending":
      default:
        return <Loader dimension={70} type="loader" />;
    }
  }, [passkeyRegistrationApprovalStatus]);

  // Effect to manage step transition based on loading state

  useEffect(() => {
    if (verified_user && !isHandleUserVerfiedCalled) {
      isHandleUserVerfiedCalled = true;
      handleVerifiedUser();
    } else if (!loading) {
      setSignInInCurrentScreen(SIGNIN);
    } else {
      setSignInInCurrentScreen(LOADING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /**
   * Determines which component to render based on the current step
   */
  const renderComponent = useMemo(() => {
    switch (signInCurrentScreen) {
      case LOADING:
        return <SignInSkelton />; // Render skeleton loader during the loading phase
      case METHODS:
        return (
          <SigninMethods
            handleBack={() => handleApprovalBackToSigninPage()} // Handle going back to the sign-in step
            handleOption={handleAuthMethodOptions} // Handle option selection
          />
        );
      case APPROVAL: {
        const { countryCode = "", value } = form?.[0] ?? {};
        const val = currentAuthMethod === "phone" ? countryCode + value : value;

        let method = currentAuthMethod as any;
        const isEmailAndPhone =
          instanceDetails?.signUp?.scope?.includes("email") &&
          instanceDetails?.signUp?.scope?.includes("phone");

        let message = t(
          `signin.approval.${currentAuthMethod}.sublabel`
        ).replace("${value}", val);
        if (isEmailAndPhone) {
          method = "email_phone";
          message = t(`signin.approval.email_phone.sublabel`).replace(
            "${value}",
            val
          );
        }
        return (
          <WatingApproval
            handleBack={() => {
              handleApprovalBackToMethod();
            }} // Handle going back to the methods step
            handleResend={() => handleLoginViaLink()}
            {...t(`signin.approval.${method}`)} // Pass translation props to WatingApproval
            //need to replace value with val
            sublabel={message}
            loader={getStatusIcon}
          />
        );
      }
      default:
        return <SigninForm />; // Render sign-in form by default
    }
  }, [
    signInCurrentScreen,
    LOADING,
    METHODS,
    handleAuthMethodOptions,
    APPROVAL,
    handleApprovalBackToSigninPage,
    form,
    currentAuthMethod,
    instanceDetails?.signUp?.scope,
    t,
    getStatusIcon,
    handleApprovalBackToMethod,
    handleLoginViaLink,
  ]);

  return (
    <Fragment>
      <div className="signin-wrapper">
        <div className="signin-wrapper__inner content-center">
          {/* Render the component based on the current step */}
          {renderComponent}{" "}
        </div>
      </div>

      {/* Browser Registration Modal */}
      <Modal
        isOpen={showBrowserRegistrationModal}
        closeModal={() => setShowBrowserRegistrationModal(false)}
        modalName="browser-registration-modal"
        showCloseBtn={false}
        showHeader={false}
        className="browser-registration-modal-wrapper"
      >
        <BrowserRegistrationModal
          onBack={() => {
            setLoadingMethodSelection({
              index: 0,
              loading: false,
            });
            // Close modal and return to signin methods
            setShowBrowserRegistrationModal(false);
            setSignInInCurrentScreen("methods");
          }}
          onContinue={async () => {
            try {
              await registerWebAuthnWithAuthSource();
              // Close browser registration modal and show waiting approval
              setShowBrowserRegistrationModal(false);
              setShowBrowserRegistrationWaitingModal(true);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(error);
            }
          }}
        />
      </Modal>

      {/* Browser Registration Waiting Approval Modal */}
      <Modal
        isOpen={showBrowserRegistrationWaitingModal}
        closeModal={() => setShowBrowserRegistrationWaitingModal(false)}
        modalName="browser-registration-waiting-modal"
        showCloseBtn={false}
        showHeader={false}
        className="browser-registration-waiting-modal-wrapper"
      >
        <WatingApproval
          handleBack={() => {
            handleApprovalBackToMethod();
            setShowBrowserRegistrationWaitingModal(false);
          }}
          styles={{ border: "none" }}
          handleResend={handleBrowserRegistrationResend}
          {...t(`signin.approval.email_phone`)}
          sublabel={t(`signin.approval.email_phone.sublabel`)}
          loader={getStatusIcon}
        />
      </Modal>
    </Fragment>
  );
};
